import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '../lib/safeToast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getApiErrorMessage } from '../lib/utils';
import { Eye, EyeOff, Lock, Loader2 } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (tokenFromUrl) setToken(tokenFromUrl);
  }, [tokenFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error('Reset link is invalid or missing. Request a new link from Forgot password.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${base}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim(), new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(getApiErrorMessage(data.detail, 'Reset failed'));
        return;
      }
      setSuccess(true);
      toast.success('Password has been reset. You can sign in now.');
    } catch (err) {
      toast.error('Request failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="PayZen" className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <h1 className="font-bold text-xl text-[#1C472B]">PayZen</h1>
              <p className="text-xs text-slate-500">Merchant Hub</p>
            </div>
          </div>

          {!success ? (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A] mb-2">Reset password</h2>
              <p className="text-slate-500 mb-8">
                Enter your new password below. Use the link from your email to get here.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {!tokenFromUrl && (
                  <div>
                    <label className="input-label">Reset token</label>
                    <Input
                      type="text"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Paste the token from your email"
                      className="input-field"
                      data-testid="reset-token"
                    />
                  </div>
                )}
                <div>
                  <label className="input-label">New password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="input-field !pl-12 pr-12"
                      minLength={8}
                      required
                      data-testid="reset-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="input-label">Confirm new password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    className="input-field"
                    required
                    data-testid="reset-confirm"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] active:bg-[#0F2818] text-white shadow-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={loading || !token.trim()}
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Reset password'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A] mb-2">Password reset</h2>
              <p className="text-slate-500 mb-8">You can now sign in with your new password.</p>
              <Button
                className="w-full h-12 rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] text-white"
                onClick={() => navigate('/login')}
                data-testid="reset-goto-login"
              >
                Sign in
              </Button>
            </>
          )}

          <p className="text-center mt-8 text-slate-500">
            <Link to="/login" className="text-[#1C472B] font-semibold hover:underline">
              Back to login
            </Link>
          </p>
        </div>
      </div>

      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1666618207644-4de0226a3f85?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="h-full w-full bg-[#1C472B]/80 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-lg">
            <h2 className="text-4xl font-extrabold mb-4">Secure Banking for Modern Merchants</h2>
            <p className="text-lg text-slate-300">
              Manage payments, track transactions, and grow your business with PayZen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
